<template>
  <div class="users">
    <div class="default-table">
      <div class="d-sm-flex p-1">
        <b-button
          v-if="$can('create', 'users', 'captain')"
          variant="bg-primary"
          @click="$router.push({ name: 'add-project-user' })"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Invite new user
        </b-button>
        <b-input-group class="w-auto mt-1 mt-sm-0 ml-sm-auto">
          <b-input-group-prepend is-text>
            <GjIcon
              name="Search"
              size="22"
              @click="onChangefilterTerm"
            />
          </b-input-group-prepend>
          <b-form-input
            id="filterInput"
            v-model.trim="filterTerm"
            type="search"
            placeholder="Type to Search"
            class="mr-0"
          />
        </b-input-group>
      </div>
      <div class="position-relative h-100 d-none d-lg-block">
        <b-table
          ref="table"
          :items="users"
          :fields="fields"
          :class="{ loaded: !loading }"
          show-empty
          responsive
          class="more-visible"
        >
          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <no-data
              v-else
              no-data-text="No users have been added yet..."
              create-text="+ Create User"
              @action="$router.push({ name: 'add-project-user' })"
            />
          </template>

          <template #cell(id)="data">
            <a
              v-if="$can('update', 'users', 'captain')"
              @click="
                $router.push({
                  name: 'edit-project-user',
                  params: { id: data.value },
                })
              "
            >
              <GjIcon
                class="icon-right"
                name="Edit"
              />
            </a>
            <template v-if="$can('delete', 'users', 'captain')">
              <b-spinner v-if="removingUserId == data.value && removingUser" />
              <a
                v-else
                @click="deleteUser(data.value)"
              >
                <GjIcon
                  class="icon-right"
                  name="Delete"
                />
              </a>
            </template>
          </template>
        </b-table>
        <span class="bg-icon">
          <GjIcon
            name="AvatarGroup"
            size="140"
            class="img-fluid"
          />
        </span>
        <table-busy v-if="loading && users.length > 0" />
      </div>
      <div class="position-relative d-lg-none px-1">
        <template v-for="user in users">
          <card
            :key="user.id"
            :fields="fields"
            :item="user"
            class="shadow"
          >
            <template #options>
              <b-dropdown
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'users', 'captain')"
                  @click="
                    $router.push({
                      name: 'edit-project-user',
                      params: { id: user.id },
                    })
                  "
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'users', 'captain')"
                  @click="deleteUser(user.id)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
          </card>
        </template>
        <div
          v-if="loading"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && users.length < 1">
          <no-data
            class="text-center"
            no-data-text="No users have been added yet..."
            create-text="+ Create User"
            @action="$router.push({ name: 'add-project-user' })"
          />
        </template>
      </div>
      <div
        v-if="users.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'Users',
  components: { Card },
  data() {
    return {
      fields: [
        'name',
        { key: 'email', isPrimary: true },
        { key: 'role', isPrimary: true },
        { key: 'id', label: 'Actions' },
      ],
      removingUserId: null,
      filterTerm: '',
      typingTimer: null,
      doneTypingInterval: 500,
    }
  },
  computed: {
    users() {
      return this.$store.state.projects.users
    },
    resultInfo() {
      return this.$store.state.projects.usersResultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo?.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    loading() {
      return this.$store.state.projects.fetchingUsers
    },
    removingUser() {
      return this.$store.state.projects.removingUser
    },
    currentPage: {
      get() {
        return this.$store.getters['projects/usersCurrentPage']
      },
      set(val) {
        this.$store.commit('projects/SET_USERS_CURRENT_PAGE', val)
      },
    },
  },
  watch: {
    currentPage() {
      this.fetchUsers()
    },
    filterTerm() {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => {
        this.onChangefilterTerm()
      }, this.doneTypingInterval)
    },
  },
  created() {
    if (this.$store.state.app.previousRouteName !== 'edit-project-user') {
      this.currentPage = 1
    }
    this.fetchUsers()
  },
  methods: {
    fetchUsers() {
      const query = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        search: this.filterTerm,
      }
      this.$store.dispatch('projects/fetchUsers', query).catch(error => {
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching users.')
      })
    },
    deleteUser(id) {
      this.confirmDelete(
        'Delete User',
        'Are you sure you want to delete this user?',
      ).then(ok => {
        if (ok) {
          this.removingUserId = id
          this.$store
            .dispatch('projects/deleteUser', id)
            .then(() => {
              this.removingUserId = null
              if (this.users.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.fetchUsers()
              }
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting user.')
            })
        }
      })
    },
    onChangefilterTerm() {
      if (this.currentPage === 1) {
        this.fetchUsers()
      } else {
        this.currentPage = 1
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.users {
  height: 100%;

  .default-table {
    margin-top: 0;
    box-shadow: none;
    height: 100%;
    display: flex;
    flex-direction: column;
    .table-responsive {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-direction: column;
    }
  }
  ::v-deep {
    .table {
      border-bottom: 0;
      margin-top: 0;
      thead {
        th:last-child {
          width: 30px;
        }
      }
      .spinner-border {
        margin-left: 5px;
        width: 15px;
        height: 15px;
      }
    }
    .loading {
      z-index: 2;
    }
  }
}
</style>
